import { Group, Stack } from '@mantine/core';
import { useReactivateOrCreateSecodaLiteSubscription } from '@repo/api-codegen';
import { useGetBillingInfo } from '@repo/api-codegen/v2';
import { Text, Title } from '@repo/foundations';
import { useCallback, useState } from 'react';
import { AuthCard } from '../../components/Auth/AuthCard';
import { AuthButton } from '../../components/Auth/AuthLayouts/AuthButton';
import { AuthTermsAndPolicy } from '../../components/Auth/AuthTermsAndPolicy';

interface OnboardingReactivationControllerProps {
	title?: string;
	description?: string;
	onReactivate?: () => void;
	onCancel?: () => void;
	buttonText?: string;
	width?: string | number;
}

export function OnboardingReactivationController({
	title,
	description,
	onReactivate,
	onCancel,
	buttonText = 'Reactivate',
	width = 480,
}: OnboardingReactivationControllerProps) {
	const [reactivationError, setReactivationError] = useState<Error | null>(
		null
	);
	const [isLoading, setIsLoading] = useState(false);

	const { data: billingInfo } = useGetBillingInfo({});
	const { mutateAsync: reactivateOrCreateSecodaLiteSubscription } =
		useReactivateOrCreateSecodaLiteSubscription();

	const handleReactivate = useCallback(async () => {
		try {
			setIsLoading(true);
			setReactivationError(null);
			await reactivateOrCreateSecodaLiteSubscription({});
			onReactivate?.();
		} catch (error) {
			setReactivationError(error as Error);
		} finally {
			setIsLoading(false);
		}
	}, [reactivateOrCreateSecodaLiteSubscription, onReactivate]);

	const handleCancel = useCallback(() => {
		onCancel?.();
	}, [onCancel]);

	// Customize messaging based on subscription status
	const getStatusSpecificContent = useCallback(() => {
		const status = billingInfo?.subscription_status;

		switch (status) {
			case 'paused':
				return {
					title: title || 'Resume your subscription',
					description:
						description ||
						'Your subscription is paused. Resume to continue using Secoda.',
					buttonText: buttonText || 'Resume Subscription',
				};
			case 'canceled':
				return {
					title: title || 'Reactivate your subscription',
					description:
						description ||
						'Your subscription was canceled. Reactivate to continue using Secoda.',
					buttonText: buttonText || 'Reactivate Subscription',
				};
			case 'ended':
			case 'unpaid':
			case 'past_due':
			case 'incomplete':
			case 'incomplete_expired':
				return {
					title: title || 'Start a new subscription',
					description:
						description ||
						'Your subscription has ended. Start a new subscription to continue using Secoda.',
					buttonText: buttonText || 'Start New Subscription',
				};
			default:
				return {
					title,
					description,
					buttonText,
				};
		}
	}, [billingInfo?.subscription_status, title, description, buttonText]);

	const {
		title: displayTitle,
		description: displayDescription,
		buttonText: displayButtonText,
	} = getStatusSpecificContent();

	return (
		<AuthCard width={width}>
			<Stack spacing="xl">
				<Stack spacing="md">
					<Title>{displayTitle}</Title>
					<Text color="text/secondary/default">{displayDescription}</Text>
				</Stack>

				{reactivationError && (
					<Text color="text/critical/default">{reactivationError.message}</Text>
				)}

				<Stack spacing="md">
					<AuthButton
						variant="primary"
						onClick={handleReactivate}
						loading={isLoading}
						style={{ width: '100%' }}
					>
						<Group align="center" position="center">
							{displayButtonText}
						</Group>
					</AuthButton>

					{onCancel && (
						<AuthButton
							variant="default"
							onClick={handleCancel}
							disabled={isLoading}
							style={{ width: '100%' }}
						>
							<Group align="center" position="center">
								Cancel
							</Group>
						</AuthButton>
					)}
				</Stack>

				<AuthTermsAndPolicy />
			</Stack>
		</AuthCard>
	);
}
